// packages/ui/src/components/kanban/KanbanColumn.tsx
// A single droppable column in the Kanban board

import { useDroppable } from '@dnd-kit/core'
import { KanbanCard } from './KanbanCard'
import type { DashboardCard } from '../../api/client'

export interface ColumnConfig {
  id: string
  label: string
  color: string
}

export interface KanbanColumnProps {
  config: ColumnConfig
  cards: DashboardCard[]
  onCardClick?: (card: DashboardCard) => void
}

export function KanbanColumn({ config, cards, onCardClick }: KanbanColumnProps) {
  const { setNodeRef, isOver } = useDroppable({ id: config.id })

  return (
    <div
      ref={setNodeRef}
      data-testid={`column-${config.id}`}
      className={`flex flex-col w-72 min-w-[18rem] shrink-0 rounded-lg transition-colors ${
        isOver
          ? 'bg-gray-900/80 ring-2 ring-blue-500/40'
          : 'bg-gray-900/50'
      }`}
    >
      {/* Column header */}
      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-gray-800">
        <div className={`w-2 h-2 rounded-full ${config.color}`} />
        <h3 className="text-sm font-semibold text-gray-200">{config.label}</h3>
        <span
          data-testid={`column-${config.id}-count`}
          className="text-xs text-gray-500 bg-gray-800 px-1.5 py-0.5 rounded ml-auto"
        >
          {cards.length}
        </span>
      </div>

      {/* Card list */}
      <div className="flex flex-col gap-2 p-2 overflow-y-auto min-h-[4rem]">
        {cards.length === 0 ? (
          <div
            data-testid={`column-${config.id}-empty`}
            className="text-xs text-gray-600 text-center py-4"
          >
            No cards
          </div>
        ) : (
          cards.map((card) => (
            <KanbanCard key={card.id} card={card} onClick={onCardClick} />
          ))
        )}
      </div>
    </div>
  )
}
